import React, {BaseSyntheticEvent, useEffect, useRef, useState} from "react";
import localStyles from "./styles.module.scss";
import icons from "../../utils/icons";
import uuid from "react-uuid";
import Modal from "../Modal";
import translate from "../../utils/translate";
import ProgressSpinner from "../ProgressSpinner";
import {COMMERCIAL_IDS} from "../../utils/constants";

const IMAGE_MIME_TYPE: RegExp = /^image\//;

interface Props {
    handleImages?: (images: any[]) => void,
    handleDeleteImage?: (image: SimpleImageInterface | ImageInterface) => void,
    className?: string,
    limitSize?: number,
    limitCount?: number,
    additionalImages?: SimpleImageInterface[],
}

export interface ImageInterface {
    // base64: string | ArrayBuffer;
    srcBlob: string;
    name: string;
    id: string;
    origin: File;
}

export interface SimpleImageInterface {
    id: string;
    name: string;
    url: string;
}

const MAX_WIDTH = 3840;
const MAX_HEIGHT = 2160;

const PhotoUploader: React.FC<Props> = ({
                                            handleImages,
                                            handleDeleteImage,
                                            className,
                                            limitSize = 0,
                                        }: Props) => {

    const filePicker = useRef(null);
    const dropContainer = useRef(null);

    const userid = localStorage.getItem('userid').toString()

    const [images, setImages] = useState([]);
    const [showModal, setShowModal] = useState<boolean>(false)
    const [currentFullscreenImage, setCurrentFullscreenImage] = useState<ImageInterface | SimpleImageInterface>(null)
    const [loadingAddImage, setLoadingAddImage] = useState<boolean>(false)

    const handlePick = () => {
        filePicker.current.click();
    }

    const filesHandler = (files: File[]) => {
        const sizeLimitBytes = limitSize * 1000000;
        setLoadingAddImage(true)
        const length = files.length

        Array.from(files)
            .filter((file: File) => !limitSize ? true : file.size <= sizeLimitBytes)
            .map((file: File, index: number) => {
                if (COMMERCIAL_IDS.includes(userid)) {
                    setImages((prevState) => [...prevState, {
                        id: uuid(),
                        name: file.name,
                        type: file.type,
                        blob: undefined,
                        file: file,
                        srcBlob: URL.createObjectURL(file)
                    }])
                    setLoadingAddImage(false);
                } else {
                    const blobURL = URL.createObjectURL(file);
                    const img = new Image();
                    img.src = blobURL;

                    img.onload = function () {
                        const [newWidth, newHeight] = calculateSize(img, MAX_WIDTH, MAX_HEIGHT);
                        const canvas = document.createElement("canvas");
                        canvas.width = newWidth;
                        canvas.height = newHeight;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0, newWidth, newHeight);
                        canvas.toBlob(
                            (blob) => {
                                setImages((prevState) => [...prevState, {
                                    id: uuid(),
                                    name: file.name,
                                    type: file.type,
                                    blob,
                                    // base64: canvas.toDataURL("image/webp")
                                    srcBlob: URL.createObjectURL(file)
                                }])
                                if ((length - 1) === index)
                                    setLoadingAddImage(false);
                            },
                            "image/jpeg",
                            0.9
                        );
                    };
                }
            })
    }

    useEffect(() => {
        handleImages && handleImages(images)
    }, [images]);

    const handleChange = (event: BaseSyntheticEvent) => {
        filesHandler(event.target.files)
    };

    const highlightOn = (event: BaseSyntheticEvent) => {
        event.stopPropagation();
        event.preventDefault();
        dropContainer.current.classList.add(localStyles.highlight)
    }

    const highlightOff = (event: BaseSyntheticEvent) => {
        event.stopPropagation();
        event.preventDefault();
        dropContainer.current.classList.remove(localStyles.highlight)
    }

    const dropHandle = (event: React.DragEvent<HTMLDivElement>) => {
        highlightOff(event);
        let files: File[] = Array.from(event.dataTransfer.files);
        files = files.filter((file) => {
            return IMAGE_MIME_TYPE.test(file.type);
        });
        if (files.length) {
            filesHandler(files)
        }
    }

    const removeImage = (image: SimpleImageInterface | ImageInterface) => {
        if (!instanceOfSimpleImageInterface(image)) {
            setImages((prevState) => prevState.filter((value) => value.id !== image.id));
        }
        handleDeleteImage && handleDeleteImage(image)
    }

    const viewFullscreenImage = (image: ImageInterface | SimpleImageInterface) => {
        setShowModal(true);
        setCurrentFullscreenImage(image)
    }

    function calculateSize(img: HTMLImageElement, maxWidth: number, maxHeight: number) {
        let width = img.width;
        let height = img.height;

        // calculate the width and height, constraining the proportions
        if (width > height) {
            if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
            }
        } else {
            if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
            }
        }
        return [width, height];
    }

    return (
        <div className={`${localStyles.container} ${className}`}>
            <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 1,
                visibility: loadingAddImage ? 'visible' : 'hidden'
            }}>
                <ProgressSpinner strokewidth={2} animationduration={COMMERCIAL_IDS.includes(userid) ? 5 : 10}/>
            </div>
            <div className={localStyles.inputFilesContainer}
                 ref={dropContainer}
            >
                <input className={localStyles.hidden}
                       type="file"
                       accept=".png,.jpg,.jpeg,.gif,.web"
                       multiple={true}
                       onChange={handleChange}
                       ref={filePicker}
                />

                <p className={`${localStyles.hideOnDesktop} ${localStyles.inputFilesLabel}`}>
                    <span
                        className={localStyles.inputFilesLabelPrimaryText}>{translate('select_a_file')}</span>
                </p>

                <p className={`${localStyles.hideOnMobile} ${localStyles.inputFilesLabel} ${localStyles.inputFilesLabelDesktop}`}>
                        <span className={localStyles.inputFilesLabelPrimaryText}>
                            {translate('drag_the_file')}
                        </span>
                    or
                </p>
                <button type="button" className={`${localStyles.hideOnMobile} ${localStyles.inputFilesButton}`}>
                    <img src={icons.paperclip} alt="Upload files"/>
                    {translate('select_a_file')}
                </button>


                <div className={localStyles.dropArea}
                     onDragEnter={highlightOn}
                     onDragOver={highlightOn}

                     onDragLeave={highlightOff}
                     onDrop={dropHandle}

                     onClick={handlePick}
                ></div>
            </div>
            {
                <ul className={localStyles.viewFilesList}>
                    {images.map((image) => {
                        return (
                            <li className={localStyles.listItem}
                                key={image.id}
                            >
                                <div className={localStyles.bgSmallImage}
                                     onClick={() => {
                                         viewFullscreenImage(image)
                                     }}
                                />
                                {/*<img src={image.base64 as string}*/}
                                <img src={image.srcBlob as string}
                                     alt={image.name}
                                     className={localStyles.uploadedImage}
                                     width={118}
                                     height={102}
                                />
                                <div className={localStyles.removeImageButton}
                                     onClick={() => {
                                         removeImage(image);
                                     }}
                                >
                                    <img src={icons.close_small}
                                         alt="Remove selected image"
                                         width={25}
                                         height={25}
                                    />
                                </div>
                                <p>{image.name}</p>
                            </li>
                        );
                    })}
                </ul>
            }

            <Modal show={showModal}
                   width={560}
                   element={<div>
                       {currentFullscreenImage && <img
                           src={((instanceOfSimpleImageInterface(currentFullscreenImage) ? currentFullscreenImage?.url : currentFullscreenImage?.srcBlob)) as string}
                           alt={currentFullscreenImage.name}
                           className={localStyles.uploadedImage}
                           style={{
                               width: '100%',
                               maxHeight: '100%'
                           }}
                       />
                       }
                   </div>}
                   closeOut
                   handleClose={() => {
                       setShowModal(false);
                       setCurrentFullscreenImage(null);
                   }}
            />

        </div>
    )
}

export default PhotoUploader;

export function instanceOfSimpleImageInterface(object: any): object is SimpleImageInterface {
    return 'name' in object && 'url' in object;
}
